import React from 'react';
import { useUrlLocalizer, useTranslator } from 'tradera/lang/translate';
import { useInView } from 'react-intersection-observer';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import type { PossibleItemTypes } from 'tradera/components/item-card/item-card';
import { ItemCard } from 'tradera/components/item-card/item-card';
import { generateItemCardPlaceholders } from 'tradera/components/item-card/generate-item-card-placeholders';
import * as itemActions from 'tradera/state/items/actions';
import uiActions from 'tradera/state/ui/actions';
import { trackContainerClick } from '../analytics';
import { Carousel } from 'tradera/components/carousel/carousel';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import { useFetchFavouriteSellers } from './use-fetch-favourite-sellers';
import { useAppDispatch } from 'tradera/state/hooks';
import { MoreResultsCard } from 'tradera/components/carousel/more-results-card/more-results-card';
import type { AnyAction } from 'redux';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';

export const SellerItems = () => {
    const { t } = useTranslator();
    const dispatch = useAppDispatch();

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        triggerOnce: true
    });

    // Important to use this hook instead of the query directly, since we leverage caching behaviour
    // between this component and Sellers
    const {
        data: favouriteSellersData,
        isLoading,
        error
    } = useFetchFavouriteSellers({
        skip: !loadingTriggerInView
    });

    const { items, totalItems } = favouriteSellersData || {};

    const moreItemsCanBeFetched =
        !items || !totalItems ? false : items.length < totalItems;

    const isMobile = useIsMobile();
    // const isDesktop = useIsDesktop();

    const displayItems =
        isLoading || !items
            ? generateItemCardPlaceholders(isMobile ? 2 : 4)
            : items;

    const toggleWishList = (itemId: number) => {
        dispatch(itemActions.wishlist(itemId));
    };

    const openQuickView = (item?: PossibleItemTypes) => {
        dispatch(
            uiActions.openQuickViewAction({
                items,
                item,
                openedFrom: 'Start Page Favourite Sellers'
            }) as unknown as AnyAction
        );
    };

    const { toLocalizedUrl } = useUrlLocalizer();

    const noItemsFromSellers = !isLoading && items?.length === 0;
    // To indicate that the list is scrollable on mobile we show 2.5 items
    const numberCarouselCards = isMobile ? 2.5 : 6;

    return (
        <StartPageSection ref={loadingTriggerRef}>
            {isLoading || noItemsFromSellers || error ? null : (
                <>
                    <StartPageSectionTitle
                        href={toLocalizedUrl('/favourite-sellers')}
                        inSpa
                        onClick={() =>
                            trackContainerClick(
                                'favouriteSeller-items',
                                'favouriteSeller-items header link'
                            )
                        }>
                        {t('startPage_favouriteSellers_title')}
                    </StartPageSectionTitle>

                    <Carousel
                        id="seller-items-carousel"
                        numberItemsToScroll={numberCarouselCards}
                        numberItemsToShow={numberCarouselCards}
                        padX
                        className="mx-md-3 mx-lg-2 mx-xl-0">
                        <LeftButton alignVerticallyToItemCards size="md" />
                        <CarouselContent>
                            {displayItems.map((item) => (
                                <ItemCard
                                    className="m-0 p-0"
                                    key={item.itemId}
                                    item={item}
                                    onAddToWishlist={toggleWishList}
                                    onQuickView={openQuickView}
                                    onRemoveFromWishlist={toggleWishList}
                                    gaCategory="Startpage favouriteSeller-items"
                                    showBidStatus
                                    showSellerAlias
                                    showTitleOnOneLine
                                    showShippingOrOnDisplay={false}
                                />
                            ))}
                            {moreItemsCanBeFetched ? (
                                <MoreResultsCard
                                    href={toLocalizedUrl('/favourite-sellers')}
                                    onClick={() =>
                                        trackContainerClick(
                                            'favouriteSeller-items',
                                            'favouriteSeller-items showAll link'
                                        )
                                    }
                                    label={t(
                                        'startPage_favouriteSellers_showMore'
                                    )}
                                />
                            ) : null}
                        </CarouselContent>
                        <RightButton alignVerticallyToItemCards size="md" />
                    </Carousel>
                </>
            )}
        </StartPageSection>
    );
};
